import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Plus, ArrowUpRight, Hourglass, CheckCircle2, XCircle, Inbox } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { NewSiteRequestDialog } from "./MesDemandesSites";

const STATUS_BADGE = {
  en_attente: { label: "En attente", Icon: Hourglass, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approuve:   { label: "Approuvée",  Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  refuse:     { label: "Refusée",    Icon: XCircle, cls: "bg-red-50 text-red-700 border-red-200" },
};

/**
 * Widget Dashboard "Demandes sites".
 * Visible pour les collaborateurs / gestionnaires uniquement (pas admin / manager).
 * Permet d'initier directement une demande matériel pour site depuis le tableau de bord
 * et de consulter rapidement les 3 dernières demandes en cours.
 * L'historique complet reste accessible dans /mon-dossier (onglet Demandes sites).
 */
export default function DemandesSitesDashboardWidget() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);

  const eligible = user?.role === "collaborateur" || user?.role === "gestionnaire";

  const load = useCallback(async () => {
    if (!eligible) return;
    setLoading(true);
    try {
      const r = await api.get("/equipment-site-requests");
      setRequests(r.data || []);
    } catch {
      // silencieux côté dashboard
    } finally {
      setLoading(false);
    }
  }, [eligible]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { en_attente: 0, approuve: 0, refuse: 0 };
    for (const r of requests) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [requests]);

  // Pour ne pas surcharger le dashboard : on n'affiche que les 3 plus récentes "en cours"
  const recentPending = useMemo(() => {
    return requests
      .filter((r) => r.status === "en_attente")
      .slice(0, 3);
  }, [requests]);

  if (!eligible) return null;

  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-6"
      data-testid="dashboard-demandes-sites-widget"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <p className="label-uppercase mb-1 inline-flex items-center gap-1.5">
            <Building2 size={11} /> Demandes sites
          </p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            Vos demandes pour sites
          </h2>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            {counts.en_attente} en attente · {counts.approuve} approuvée(s) · {counts.refuse} refusée(s)
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/mon-dossier?tab=site-req"
            className="text-sm text-[#0055FF] hover:underline flex items-center gap-1"
            data-testid="demandes-sites-history-link"
          >
            Voir l&apos;historique <ArrowUpRight size={14} />
          </Link>
          <Button
            type="button" size="sm"
            onClick={() => setOpenCreate(true)}
            className="bg-[#0055FF] hover:bg-[#0044CC]"
            data-testid="demandes-sites-quick-new-btn"
          >
            <Plus size={13} className="mr-1.5" /> Nouvelle demande
          </Button>
        </div>
      </div>

      {loading && requests.length === 0 ? (
        <p className="text-sm text-zinc-500 font-mono py-3">Chargement…</p>
      ) : recentPending.length === 0 ? (
        <div className="py-6 text-center" data-testid="demandes-sites-empty">
          <Inbox className="mx-auto text-zinc-300 mb-2" size={28} />
          <p className="text-sm text-zinc-500 font-mono">
            {requests.length === 0
              ? "Aucune demande en cours."
              : "Aucune demande en attente."}
          </p>
          <p className="text-xs text-zinc-400 font-mono mt-1">
            Vous pouvez initier une demande pour un site où vous avez un shift dans les 7 prochains jours.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {recentPending.map((r) => {
            const b = STATUS_BADGE[r.status] || STATUS_BADGE.en_attente;
            const Icon = b.Icon;
            const isRempl = r.request_type === "remplacement";
            return (
              <div
                key={r.id}
                className="py-2.5 flex items-center justify-between gap-2 flex-wrap"
                data-testid={`demandes-sites-quick-row-${r.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-zinc-50 border-zinc-200 text-zinc-700">
                      {isRempl ? "Remplacement" : "Nouveau besoin"}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${b.cls}`}>
                      <Icon size={10} /> {b.label}
                    </span>
                    <p className="text-sm font-medium text-zinc-900 truncate">{r.site_name || "—"}</p>
                  </div>
                  <p className="text-xs text-zinc-600 font-mono truncate">
                    {isRempl
                      ? `Remplacement de ${r.designation_snapshot || "—"} · qté ${r.quantite_demandee}`
                      : `${r.besoin_titre} · qté ${r.quantite_demandee}`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <NewSiteRequestDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={() => { setOpenCreate(false); load(); }}
      />
    </div>
  );
}
